import { Fragment, useCallback, useState } from 'react';
import {
  ArrowRight,
  ArrowSquareOut,
  CalendarBlank,
  CaretDown,
  CaretRight,
  Microphone,
  Robot,
  Student,
  Trash,
  Warning,
  Wrench,
} from '@phosphor-icons/react';

import { Chip, CopyButton, IdRow, JsonBlock } from './conversation-bits';
import { CommentAnchor, type ConversationReviewSurface } from './conversation-comments';
import Markdown from '../markdown';
import { Link } from '../../lib/routing';
import {
  AUDIT_TIME_ZONE_LABEL,
  auditDayKey,
  fmtAuditAbsolute,
  fmtAuditDateTime,
  fmtAuditDay,
  fmtAuditTime,
} from '../../lib/audit-time';
import {
  SIGNAL_BY_ID,
  activeSignals,
  fmtDuration,
  inlinePreview,
  messageSignals,
  surfaceOf,
  toolStatus,
  TOOL_STATUS_LABEL,
  TOOL_STATUS_TONE,
  type AdminConversationDetail,
  type AdminConversationMessage,
  type AdminConversationRow,
  type AdminConversationToolCall,
  type AdminConversationUsage,
} from '../../lib/admin-conversations';

const TH = 'px-2 py-[7px] font-black text-[10px] tracking-[0.8px] text-[var(--naia-text-secondary)] uppercase';

export function conversationPath(row: Pick<AdminConversationRow, 'id'>): string {
  return `/admin/conversations/${row.id}`;
}

export function RelativeDate({ iso }: { iso: string }) {
  const abs = new Date(iso);
  const diffMin = Math.floor((Date.now() - abs.getTime()) / 60_000);
  let rel: string;
  if (diffMin < 1) rel = 'maintenant';
  else if (diffMin < 60) rel = `il y a ${diffMin} min`;
  else if (diffMin < 1440) rel = `il y a ${Math.floor(diffMin / 60)} h`;
  else rel = `il y a ${Math.floor(diffMin / 1440)} j`;
  return (
    <span title={fmtAuditAbsolute(iso)} className="cursor-default whitespace-nowrap">
      {rel}
    </span>
  );
}

export function SurfaceBadge({ row }: { row: Pick<AdminConversationRow, 'mode'> }) {
  const surface = surfaceOf(row);
  return (
    <span
      title={surface.hint}
      className="inline-block shrink-0 rounded-[4px] bg-[var(--naia-info-bg)] px-[6px] py-[2px] text-[10px] font-extrabold uppercase tracking-[0.5px] text-[var(--naia-blue)]"
    >
      {surface.label}
    </span>
  );
}

/* Le vocal est un épisode dans un fil, pas un type de fil : plusieurs sessions
   peuvent vivre dans un fil de mode `general` ou `cours`, et aucun fil de
   staging ni de prod ne porte mode='voice'. Le seul marqueur fiable est
   usage_records.kind = 'voice'. */
export function VoiceBadge({ row }: { row: AdminConversationRow }) {
  if (row.voice_sessions === 0) return null;
  return (
    <span
      title={`${row.voice_sessions} session(s) vocale(s) — ${fmtDuration(row.voice_ms)}, ${row.audio_tokens.toLocaleString('fr-FR')} tokens audio`}
      className="inline-flex shrink-0 items-center gap-1 rounded-[4px] bg-[var(--naia-warn-bg)] px-[6px] py-[2px] text-[10px] font-extrabold uppercase tracking-[0.5px] text-[var(--naia-tertiary)]"
    >
      <Microphone size={10} weight="fill" />
      {row.voice_sessions} vocal
    </span>
  );
}

/* La liste est une surface de triage : on n'y montre que ce qui doit décider
   d'ouvrir un fil. « Tour sans outil » est présent presque partout et noierait
   les signaux qui comptent ; il reste filtrable et visible dans le détail. */
const LIST_MUTED = new Set(['zero_tool', 'no_message']);

export function SignalChips({ row }: { row: AdminConversationRow }) {
  const signals = activeSignals(row).filter((s) => !LIST_MUTED.has(s.id));
  if (signals.length === 0) {
    return <span className="text-[11px] text-[var(--naia-text-secondary)] opacity-60">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {signals.map((s) => (
        <Chip key={s.id} tone={s.tone} title={s.help}>
          {s.tone === 'danger' && <Warning size={10} weight="fill" />}
          {s.value} {s.short}
        </Chip>
      ))}
    </div>
  );
}

function Title({ row }: { row: AdminConversationRow }) {
  const text = row.title?.trim() || row.preview?.trim() || 'Sans titre';
  return <span className="font-semibold text-[var(--naia-text-primary)]">{text}</span>;
}

export function Cost({ row }: { row: AdminConversationRow }) {
  if (row.usage_record_count === 0) {
    return (
      <span
        className="text-[var(--naia-text-secondary)] opacity-60"
        title="Aucune ligne usage_records rattachée à ce fil — la dépense n'a pas été enregistrée, elle n'est pas nulle."
      >
        non mesuré
      </span>
    );
  }
  if (row.cost_usd === null) {
    return (
      <span className="text-[var(--naia-text-secondary)]" title="Aucun tarif connu pour ces appels.">
        non tarifé
      </span>
    );
  }
  return <span className="tabular-nums">${row.cost_usd.toFixed(4)}</span>;
}

function CardSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-4">
          <div className="mb-2 h-[12px] w-[45%] rounded bg-[var(--naia-line)]" />
          <div className="h-[10px] w-[70%] rounded bg-[var(--naia-line)]" />
        </div>
      ))}
    </>
  );
}

function DeleteThreadButton({
  row,
  onDelete,
  className,
}: {
  row: AdminConversationRow;
  onDelete: (row: AdminConversationRow) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label="Supprimer la conversation"
      title="Supprimer la conversation"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDelete(row);
      }}
      className={
        className ??
        'inline-flex h-8 w-8 items-center justify-center rounded-[var(--naia-r-sm)] text-[var(--naia-text-secondary)] transition-colors hover:bg-[var(--naia-error-bg)] hover:text-[var(--naia-error)]'
      }
    >
      <Trash size={14} weight="bold" />
    </button>
  );
}

export function ConversationRows({
  items,
  loading,
  onDelete,
}: {
  items: AdminConversationRow[];
  loading?: boolean;
  onDelete?: (row: AdminConversationRow) => void;
}) {
  return (
    <>
      <div className="mb-4 hidden overflow-hidden rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] shadow-[var(--naia-shadow-card)] lg:block">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-[var(--naia-line)] bg-[var(--naia-card-alt)]">
                <th className={`text-left ${TH}`}>Quand</th>
                <th className={`text-left ${TH}`}>Conversation</th>
                <th className={`text-left ${TH}`}>Utilisateur</th>
                <th className={`text-right ${TH}`}>Msg</th>
                <th className={`text-right ${TH}`}>Outils</th>
                <th className={`text-left ${TH}`}>Signaux</th>
                <th className={`text-right ${TH}`}>Coût</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--naia-line)]">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-[var(--naia-text-secondary)]">
                    Chargement…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-[var(--naia-text-secondary)]">
                    Aucune conversation.
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-[var(--naia-card-alt)]">
                    <td className="px-2 py-[8px] align-top text-[var(--naia-text-secondary)]">
                      <RelativeDate iso={row.last_message_at} />
                    </td>
                    <td className="max-w-[380px] px-2 py-[8px] align-top">
                      <Link to={conversationPath(row)} className="block hover:underline">
                        <Title row={row} />
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <SurfaceBadge row={row} />
                        <VoiceBadge row={row} />
                        {row.document_id && (
                          <span
                            className="truncate text-[11px] text-[var(--naia-text-secondary)]"
                            title={row.document_title ?? row.document_id}
                          >
                            {row.document_title ?? row.document_id.slice(0, 8)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="max-w-[180px] px-2 py-[8px] align-top">
                      <span className="block truncate text-[var(--naia-text-secondary)]" title={row.user_email ?? row.user_id ?? ''}>
                        {row.user_email ?? row.user_id?.slice(0, 8) ?? '—'}
                      </span>
                    </td>
                    <td className="px-2 py-[8px] text-right align-top tabular-nums">
                      {row.message_count}
                      <span className="ml-1 text-[10.5px] text-[var(--naia-text-secondary)]">
                        {row.user_messages}/{row.assistant_messages}
                      </span>
                    </td>
                    <td className="px-2 py-[8px] text-right align-top tabular-nums">
                      {row.tool_calls}
                      {row.max_calls_in_turn > 0 && (
                        <span
                          className="ml-1 text-[10.5px] text-[var(--naia-text-secondary)]"
                          title={`Maximum d'appels dans un tour : ${row.max_calls_in_turn}${row.round_cap ? ` — plafond de tours : ${row.round_cap}` : ''}`}
                        >
                          max {row.max_calls_in_turn}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[300px] px-2 py-[8px] align-top">
                      <SignalChips row={row} />
                    </td>
                    <td className="px-2 py-[8px] text-right align-top text-[11.5px]">
                      <Cost row={row} />
                    </td>
                    <td className="px-2 py-[8px] align-top">
                      <div className="flex items-center justify-end gap-1">
                        {onDelete && <DeleteThreadButton row={row} onDelete={onDelete} />}
                        <Link
                          to={conversationPath(row)}
                          aria-label="Ouvrir la conversation"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--naia-r-sm)] text-[var(--naia-text-secondary)] transition-colors hover:bg-[var(--naia-info-bg)] hover:text-[var(--naia-blue)]"
                        >
                          <ArrowRight size={14} weight="bold" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-2 lg:hidden">
        {loading ? (
          <CardSkeleton />
        ) : items.length === 0 ? (
          <div className="rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] px-4 py-8 text-center text-[13px] text-[var(--naia-text-secondary)]">
            Aucune conversation.
          </div>
        ) : (
          items.map((row) => (
            <Link
              key={row.id}
              to={conversationPath(row)}
              className="block rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-3 shadow-[var(--naia-shadow-card)] transition-colors hover:border-[var(--naia-blue)]"
            >
              <div className="mb-1.5 flex items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <SurfaceBadge row={row} />
                  <VoiceBadge row={row} />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-[var(--naia-text-secondary)]">
                    <RelativeDate iso={row.last_message_at} />
                  </span>
                  {onDelete && (
                    <DeleteThreadButton
                      row={row}
                      onDelete={onDelete}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-[var(--naia-r-sm)] text-[var(--naia-text-secondary)] transition-colors hover:bg-[var(--naia-error-bg)] hover:text-[var(--naia-error)]"
                    />
                  )}
                </div>
              </div>
              <div className="text-[13.5px] leading-snug">
                <Title row={row} />
              </div>
              <div className="mt-1 text-[11.5px] text-[var(--naia-text-secondary)]">
                {row.user_email ?? row.user_id?.slice(0, 8) ?? '—'}
                {row.document_title && ` · ${row.document_title}`}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-[var(--naia-text-secondary)]">
                <span className="tabular-nums">{row.message_count} msg</span>
                <span className="tabular-nums">{row.tool_calls} appels</span>
                <Cost row={row} />
              </div>
              <div className="mt-2">
                <SignalChips row={row} />
              </div>
            </Link>
          ))
        )}
      </div>
    </>
  );
}

function fmtDelta(ms: number): string {
  if (ms < 1000) return `+${ms} ms`;
  if (ms < 60_000) return `+${(ms / 1000).toFixed(1)} s`;
  if (ms < 3_600_000) return `+${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`;
  if (ms < 86_400_000) return `+${Math.floor(ms / 3_600_000)} h ${Math.round((ms % 3_600_000) / 60_000)} min`;
  return `+${Math.floor(ms / 86_400_000)} j ${Math.round((ms % 86_400_000) / 3_600_000)} h`;
}

function DaySeparator({ iso, showZone }: { iso: string; showZone: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-px min-w-[10px] flex-1 bg-[var(--naia-line)]" />
      <span className="inline-flex flex-wrap items-center justify-center gap-x-1.5 rounded-full border border-[var(--naia-line)] bg-[var(--naia-card-alt)] px-2.5 py-[3px] text-center text-[11px] font-bold text-[var(--naia-text-secondary)]">
        <CalendarBlank size={11} weight="bold" />
        {fmtAuditDay(iso)}
        {showZone && <span className="font-semibold opacity-80">· {AUDIT_TIME_ZONE_LABEL}</span>}
      </span>
      <span className="h-px min-w-[10px] flex-1 bg-[var(--naia-line)]" />
    </div>
  );
}

function UsageStrip({ usage }: { usage: AdminConversationUsage[] }) {
  if (usage.length === 0) {
    return (
      <span
        className="text-[11px] text-[var(--naia-text-secondary)] opacity-60"
        title="Aucune ligne usage_records ne tombe dans la fenêtre de rattachement de ce tour. La dépense n'a pas été enregistrée — elle n'est pas nulle."
      >
        coût non enregistré
      </span>
    );
  }
  const t = usage.reduce(
    (a, u) => ({
      input: a.input + u.input_tokens,
      output: a.output + u.output_tokens,
      thinking: a.thinking + u.thinking_tokens,
      cost: u.estimated_cost_usd === null ? a.cost : (a.cost ?? 0) + u.estimated_cost_usd,
    }),
    { input: 0, output: 0, thinking: 0, cost: null as number | null },
  );
  const models = [...new Set(usage.map((u) => u.model).filter(Boolean))].join(', ');
  return (
    <span className="text-[11px] tabular-nums text-[var(--naia-text-secondary)]" title={models || undefined}>
      {t.input.toLocaleString('fr-FR')} tok in · {t.output.toLocaleString('fr-FR')} out
      {t.thinking > 0 && ` · ${t.thinking.toLocaleString('fr-FR')} thinking`}
      {t.cost !== null && ` · $${t.cost.toFixed(5)}`}
      {models && ` · ${models}`}
    </span>
  );
}

function ToolCall({ call, generation }: { call: AdminConversationToolCall; generation: number }) {
  const status = toolStatus(call);
  const tone = TOOL_STATUS_TONE[status];
  const notable = status === 'error' || status === 'empty';
  const forced = generation > 0;

  return (
    <div
      className={`rounded-[var(--naia-r-sm)] border p-2 ${
        notable ? 'border-[var(--naia-error)] bg-[var(--naia-card-alt)]' : 'border-[var(--naia-line)] bg-[var(--naia-card)]'
      }`}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="rounded bg-[var(--naia-card-alt)] px-1.5 py-[1px] text-[10.5px] font-black tabular-nums text-[var(--naia-text-secondary)]">
          {call.index + 1}
        </span>
        <code className="break-all font-[var(--naia-font-mono)] text-[12.5px] font-bold text-[var(--naia-text-primary)]">
          {call.name ?? 'outil sans nom'}
        </code>
        <Chip tone={tone}>
          {status === 'error' && <Warning size={10} weight="fill" />}
          {TOOL_STATUS_LABEL[status]}
        </Chip>
        {call.tool_call_id && (
          <span className="text-[10.5px] text-[var(--naia-text-secondary)] opacity-70" title="toolCallId">
            {call.tool_call_id}
          </span>
        )}
      </div>

      <div className="grid gap-1.5 lg:grid-cols-2">
        <JsonBlock key={`args-${generation}`} label="Arguments" value={call.args} defaultOpen={forced || notable} />
        {call.has_result ? (
          <JsonBlock key={`res-${generation}`} label="Résultat" value={call.result} tone={tone} defaultOpen={forced || notable} />
        ) : (
          <div className="rounded-[var(--naia-r-sm)] border border-dashed border-[var(--naia-line)] bg-[var(--naia-card-alt)] px-2 py-2 text-[11.5px] text-[var(--naia-text-secondary)]">
            <strong className="font-bold">Résultat non conservé.</strong> Cet appel est antérieur à la persistance
            des résultats (#375, 23 août 2026) : la trace ne porte que le nom et les arguments. Ce n'est pas un
            résultat vide — l'outil a pu renvoyer n'importe quoi.
          </div>
        )}
      </div>

      {!notable && (
        <div className="mt-1 truncate text-[11px] text-[var(--naia-text-secondary)]" title={inlinePreview(call.args, 600)}>
          {inlinePreview(call.args)}
        </div>
      )}
    </div>
  );
}

function MessageCard({
  message,
  previous,
  roundCap,
  generation,
  rendered,
  review,
}: {
  message: AdminConversationMessage;
  previous: AdminConversationMessage | null;
  roundCap: number | null;
  generation: number;
  rendered: boolean;
  review?: ConversationReviewSurface;
}) {
  const isAssistant = message.role === 'assistant';
  const signals = messageSignals(message, roundCap);
  const delta = previous ? Date.parse(message.created_at) - Date.parse(previous.created_at) : null;
  const flagged = signals.some((id) => SIGNAL_BY_ID.get(id)?.tone === 'danger');

  return (
    <article
      id={`msg-${message.id}`}
      className={`scroll-mt-24 rounded-[var(--naia-r-card)] border bg-[var(--naia-card)] p-3 shadow-[var(--naia-shadow-card)] ${
        flagged ? 'border-[var(--naia-error)]' : 'border-[var(--naia-line)]'
      }`}
    >
      <header className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={`inline-flex items-center gap-1 rounded-[4px] px-[6px] py-[2px] text-[10px] font-extrabold uppercase tracking-[0.5px] ${
            isAssistant ? 'bg-[var(--naia-info-bg)] text-[var(--naia-blue)]' : 'bg-[var(--naia-card-alt)] text-[var(--naia-text-secondary)]'
          }`}
        >
          {isAssistant ? <Robot size={11} weight="fill" /> : <Student size={11} weight="fill" />}
          {isAssistant ? 'assistant' : 'utilisateur'}
        </span>
        <span
          className="text-[11.5px] tabular-nums text-[var(--naia-text-secondary)]"
          title={fmtAuditAbsolute(message.created_at)}
        >
          {fmtAuditTime(message.created_at)}
        </span>
        {delta !== null && delta > 0 && (
          <span className="text-[11px] tabular-nums text-[var(--naia-text-secondary)] opacity-70">{fmtDelta(delta)}</span>
        )}
        {isAssistant && (
          <span className="inline-flex items-center gap-1 text-[11.5px] tabular-nums text-[var(--naia-text-secondary)]">
            <Wrench size={11} />
            {message.tools.length} appel{message.tools.length > 1 ? 's' : ''}
          </span>
        )}
        {isAssistant && <UsageStrip usage={message.usage} />}
        <span className="ml-auto">
          <CopyButton value={message.id} label="ID" />
        </span>
      </header>

      {signals.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {signals.map((id) => {
            const def = SIGNAL_BY_ID.get(id);
            if (!def) return null;
            return (
              <Chip key={id} tone={def.tone} title={def.help}>
                {def.tone === 'danger' && <Warning size={10} weight="fill" />}
                {def.label}
              </Chip>
            );
          })}
        </div>
      )}

      {message.transcript.trim() ? (
        rendered ? (
          <div className="text-[13.5px] leading-[1.6] text-[var(--naia-text-primary)]">
            <Markdown text={message.transcript} />
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words text-[13.5px] leading-[1.6] text-[var(--naia-text-primary)]">
            {message.transcript}
          </p>
        )
      ) : (
        <p className="text-[13px] italic text-[var(--naia-text-secondary)]">Transcript vide.</p>
      )}

      {message.audio_path && (
        <div className="mt-2 text-[11.5px] text-[var(--naia-text-secondary)]">
          audio : <code className="break-all font-[var(--naia-font-mono)]">{message.audio_path}</code>
        </div>
      )}

      {message.tools.length > 0 && (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {message.tools.map((call) => (
            <ToolCall key={call.index} call={call} generation={generation} />
          ))}
        </div>
      )}

      <div className="mt-2 grid gap-1.5 lg:grid-cols-2">
        {message.metadata == null ? (
          <p className="self-center text-[11px] text-[var(--naia-text-secondary)] opacity-70">
            Aucune métadonnée sur ce message.
          </p>
        ) : (
          <JsonBlock key={`meta-${generation}`} label="Métadonnées brutes" value={message.metadata} defaultOpen={generation > 0} />
        )}
        {message.attachments != null && (
          <JsonBlock key={`att-${generation}`} label="Pièces jointes" value={message.attachments} defaultOpen={generation > 0} />
        )}
        {message.usage.length > 0 && (
          <JsonBlock key={`usg-${generation}`} label="usage_records" value={message.usage} defaultOpen={generation > 0} />
        )}
      </div>

      {review && (
        <CommentAnchor
          messageId={message.id}
          review={review}
          addLabel="Commenter ce tour"
        />
      )}
    </article>
  );
}

function Summary({ detail }: { detail: AdminConversationDetail }) {
  const row = detail.conversation;
  const signals = activeSignals(row);

  const jumpTo = useCallback(
    (id: string) => {
      const target = detail.messages.find((m) => messageSignals(m, row.round_cap).includes(id));
      if (!target) return;
      document.getElementById(`msg-${target.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [detail.messages, row.round_cap],
  );

  return (
    <section className="mb-4 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-4 shadow-[var(--naia-shadow-card)]">
      {signals.length > 0 ? (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {signals.map((s) => (
            <Chip key={s.id} tone={s.tone} title={`${s.help} — cliquer pour aller au premier cas`} onClick={() => jumpTo(s.id)}>
              {s.tone === 'danger' && <Warning size={10} weight="fill" />}
              {s.value} × {s.label}
            </Chip>
          ))}
        </div>
      ) : (
        <p className="mb-3 text-[12.5px] text-[var(--naia-text-secondary)]">
          Aucun signal : pas d'erreur d'outil, pas de résultat vide, pas d'annonce sans action.
        </p>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px] sm:grid-cols-3 lg:grid-cols-6">
        {[
          ['Messages', `${row.message_count}`, `${row.user_messages} utilisateur / ${row.assistant_messages} assistant`],
          ['Appels d’outils', `${row.tool_calls}`, `max ${row.max_calls_in_turn} par tour`],
          ['Plafond de tours', row.round_cap === null ? '—' : `${row.round_cap}`, `${row.turns_at_cap} tour(s) au plafond`],
          ['Erreurs', `${row.tool_errors}`, `${row.empty_results} résultat(s) vide(s)`],
          [
            'Résultats non conservés',
            `${row.missing_results}`,
            row.missing_results > 0 ? 'appels antérieurs au 23/08' : 'trace complète',
          ],
          [
            'Coût',
            row.usage_record_count === 0 ? 'non mesuré' : row.cost_usd === null ? 'non tarifé' : `$${row.cost_usd.toFixed(5)}`,
            `${row.input_tokens.toLocaleString('fr-FR')} in / ${row.output_tokens.toLocaleString('fr-FR')} out`,
          ],
        ].map(([label, value, sub]) => (
          <div key={label}>
            <dt className="text-[10px] font-black uppercase tracking-[0.6px] text-[var(--naia-text-secondary)]">{label}</dt>
            <dd className="text-[16px] font-extrabold leading-tight tabular-nums text-[var(--naia-text-primary)]">{value}</dd>
            <dd className="text-[11px] text-[var(--naia-text-secondary)]">{sub}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Identity({ detail }: { detail: AdminConversationDetail }) {
  const [open, setOpen] = useState(true);
  const row = detail.conversation;
  const surface = surfaceOf(row);

  return (
    <section className="mb-4 overflow-hidden rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] shadow-[var(--naia-shadow-card)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-[44px] w-full items-center gap-2 px-4 text-left text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)] transition-colors hover:text-[var(--naia-blue)]"
      >
        {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
        Identifiants et métadonnées
      </button>
      {open && (
        <div className="px-4 pb-3">
          <IdRow label="Fil" value={row.id} />
          <IdRow
            label="Document"
            value={row.document_id}
            extra={
              row.document_id ? (
                <Link
                  to={`/teacher/library/${row.document_id}`}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--naia-blue)] hover:underline"
                >
                  {row.document_title ?? 'Ouvrir'} <ArrowSquareOut size={11} />
                </Link>
              ) : undefined
            }
          />
          <IdRow
            label="Utilisateur"
            value={row.user_id}
            extra={row.user_email ? <span className="text-[12px] text-[var(--naia-text-secondary)]">{row.user_email}</span> : undefined}
          />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 text-[11.5px] text-[var(--naia-text-secondary)]">
            <span title={surface.hint}>
              Surface : <strong className="text-[var(--naia-text-primary)]">{surface.label}</strong> (mode « {row.mode} »)
            </span>
            {row.document_kind && <span>Type de document : {row.document_kind}</span>}
            {row.voice_sessions > 0 && (
              <span title="Sessions vocales hébergées par ce fil (usage_records.kind = 'voice'). Le vocal du web ne crée pas de fil dédié.">
                Vocal : <strong className="text-[var(--naia-text-primary)]">{row.voice_sessions} session(s)</strong> —{' '}
                {fmtDuration(row.voice_ms)}, {row.audio_tokens.toLocaleString('fr-FR')} tokens audio
              </span>
            )}
            <span title={fmtAuditAbsolute(row.created_at)}>Créée le {fmtAuditDateTime(row.created_at)}</span>
            <span title={fmtAuditAbsolute(row.last_message_at)}>
              Dernier message {fmtAuditDateTime(row.last_message_at)}
            </span>
            {row.archived_at && (
              <span title={fmtAuditAbsolute(row.archived_at)}>Archivée le {fmtAuditDateTime(row.archived_at)}</span>
            )}
            <span>Horodatage en {AUDIT_TIME_ZONE_LABEL}</span>
          </div>
        </div>
      )}
    </section>
  );
}

export function ConversationDetailView({
  detail,
  generation,
  rendered = false,
  review,
}: {
  detail: AdminConversationDetail;
  generation: number;
  /* Absente = écran de lecture seule, strictement identique à ce qu'il était.
     C'est ce qui garde la preview de conception rendable hors session. */
  review?: ConversationReviewSurface;
  /* Par défaut le transcript est montré tel qu'il est stocké — c'est le contrat
     d'un outil d'audit. Le rendu Markdown est une aide à la lecture, pas la
     source de vérité. */
  rendered?: boolean;
}) {
  return (
    <>
      <Summary detail={detail} />
      <Identity detail={detail} />

      {detail.messages.length === 0 ? (
        <p className="text-[13px] text-[var(--naia-text-secondary)]">Cette conversation ne porte aucun message.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {detail.messages.map((message, i) => {
            const previous = i > 0 ? (detail.messages[i - 1] ?? null) : null;
            const opensDay = previous === null || auditDayKey(previous.created_at) !== auditDayKey(message.created_at);
            return (
              <Fragment key={message.id}>
                {opensDay && <DaySeparator iso={message.created_at} showZone={i === 0} />}
                <MessageCard
                  message={message}
                  previous={previous}
                  roundCap={detail.conversation.round_cap}
                  generation={generation}
                  rendered={rendered}
                  review={review}
                />
              </Fragment>
            );
          })}
        </div>
      )}

      {review && (
        <section className="mt-4 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-4 shadow-[var(--naia-shadow-card)]">
          <h2 className="mb-1 text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
            Commentaire sur la conversation entière
          </h2>
          {/* Volontairement ici et pas sur chaque tour : un commentaire ancré
              sur un message désigne quelque chose de reproductible, un
              commentaire de fil ne se rattache à rien. Celui-ci existe pour ce
              qui ne tient à aucun tour, pas comme voie par défaut. */}
          <p className="text-[12px] leading-[1.5] text-[var(--naia-text-secondary)]">
            Pour ce qui ne tient à aucun tour précis. Un constat qui vise une réponse — source inventée, action
            annoncée non faite — se commente sur le tour concerné : c'est ce qui le rend exploitable plus tard.
          </p>
          <CommentAnchor
            messageId={null}
            review={review}
            addLabel="Commenter la conversation"
            emptyLabel="Aucun commentaire sur l'ensemble du fil."
          />
        </section>
      )}

      {detail.orphan_usage.length > 0 && (
        <section className="mt-4 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-4 shadow-[var(--naia-shadow-card)]">
          <h2 className="mb-2 text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
            Consommation non rattachée ({detail.orphan_usage.length})
          </h2>
          <p className="mb-2 text-[12px] text-[var(--naia-text-secondary)]">
            Ces lignes <code className="font-[var(--naia-font-mono)]">usage_records</code> portent bien l'identifiant
            de ce fil, mais aucun message d'assistant ne les précède dans la fenêtre de rattachement. Elles sont
            montrées telles quelles plutôt que rattachées à tort.
          </p>
          <JsonBlock key={`orphan-${generation}`} label="Lignes" value={detail.orphan_usage} defaultOpen />
        </section>
      )}
    </>
  );
}
